/**
 * Comment Render
 * Handles all comment HTML rendering in TypeScript
 * Migrated from Blade functions for consistency and maintainability
 */

import { CommonUtils } from "../util/common-utils";
import { toPositiveIntOrNull } from "../util/id-utils";
import { dropdownRender } from "../util/dropdown/dropdown-render";
import { timestampHelper } from "../core/timestamp/timestamp";
import { phpUrl } from "../core/php-data-access";
import { Roles } from "../constants/roles";
import { commentForm } from "./comment-form";

export type CommentItemId = number;
type CommentMenuType = "post" | "comment";

export interface CommentUser {
    id?: CommentItemId;
    user_id?: CommentItemId;
    name?: string;
    display_name?: string;
    avatar?: string;
}

export interface RenderableComment {
    comment_id?: CommentItemId;
    id?: CommentItemId;
    user_id?: CommentItemId;
    user?: CommentUser;
    content?: string;
    created_at?: string;
    updated_at?: string;
    board_id?: string;
    post_id?: number;
    parent_comment_id?: CommentItemId | null;
    is_liked?: boolean;
    likes_count?: number;
    like_count?: number;
    replies_count?: number;
    replies?: RenderableComment[];
}

class CommentRender {
    /**
     * Render user avatar
     */
    public renderAvatar(user?: CommentUser): string {
        if (user?.avatar) {
            const avatarUrl = phpUrl.avatar(user.avatar);
            if (avatarUrl) {
                return `<img src="${avatarUrl}" alt="Avatar" class="avatar-img">`;
            }
        }
        const userId = user?.user_id ?? user?.id;
        // Mirrors AvatarHelper::colorIndexForUserId() (PHP) for the same deterministic
        // per-user placeholder color; unattributed/anonymous authors get no color class.
        const colorClass = userId != null ? ` avatar-placeholder--color-${Math.abs(Number(userId) || 0) % 10}` : "";
        return `<div class="avatar-placeholder${colorClass}"><i class="bi bi-person-circle"></i></div>`;
    }

    /**
     * Render dropdown menu (delegates to DropdownRender)
     */
    public renderDropdownMenu(type: CommentMenuType, itemId: CommentItemId, userId?: CommentItemId): string {
        // Use DropdownRender for consistent dropdown generation
        if (dropdownRender?.generateCommentDropdownMenu) {
            const comment = {
                comment_id: itemId,
                id: itemId,
                user_id: userId,
            };
            void type;
            return dropdownRender.generateCommentDropdownMenu(comment);
        }
        return "";
    }

    /**
     * Render individual comment item
     */
    public renderCommentItem(comment: RenderableComment): string {
        const commentId = comment.comment_id || comment.id;
        const userId = comment.user_id || comment.user?.id || comment.user?.user_id;
        const userName = comment.user ? comment.user.display_name || comment.user.name : "Anonymous";
        const commentContent = comment.content || "";
        const createdAt = comment.created_at || "";

        // Get board_id and post_id with multiple fallbacks
        let boardId = comment.board_id;
        let postId = comment.post_id;

        if (!boardId || !postId) {
            // Try to get from comment form
            const topCommentForm = commentForm.getCommentForm(document);
            if (topCommentForm) {
                boardId = boardId || topCommentForm.dataset.boardId;
                postId = postId || toPositiveIntOrNull(topCommentForm.dataset.postId) || undefined;
            }
        }

        if (!boardId || !postId) {
            // Try to get from existing like buttons
            const existingLikeBtn = commentForm.getAnyLikeButton(document);
            if (existingLikeBtn) {
                boardId = boardId || existingLikeBtn.dataset.boardId;
                postId = postId || toPositiveIntOrNull(existingLikeBtn.dataset.postId) || undefined;
            }
        }

        // Like state
        const isLiked = comment.is_liked || false;
        const likesCount = comment.likes_count || 0;
        const likeClass = isLiked ? "liked" : "";
        const iconClass = isLiked ? "bi-heart-fill" : "bi-heart";
        const likeTitle = isLiked ? "Unlike" : "Like";

        // Replies count
        const repliesCount = comment.replies_count || 0;

        return `
        <div class="comment-item" data-role="${Roles.COMMENT_ITEM}" data-comment-id="${commentId}">
            <div class="comment-header">
                <div class="comment-avatar">
                    ${this.renderAvatar(comment.user)}
                </div>
                <div class="comment-content">
                    <div class="comment-dropdown-container" data-role="${Roles.COMMENT_DROPDOWN_CONTAINER}" data-comment-id="${commentId}" data-user-id="${userId ?? ""}">
                        ${this.renderDropdownMenu("comment", commentId, userId)}
                    </div>
                    <div class="comment-user-info">
                        <span class="comment-username">${CommonUtils.escapeHtml(userName)}</span>
                        <span class="comment-timestamp timestamp-relative" data-role="${Roles.COMMENT_TIMESTAMP}" data-pending-display="1" data-timestamp="${createdAt}" title="${CommonUtils.utcToLocale(createdAt)}" style="opacity: 0;">${timestampHelper.getRelativeTime(createdAt)}</span>
                    </div>
                    <div class="comment-content-wrapper">
                        <div class="comment-text" data-role="${Roles.COMMENT_TEXT}">${commentContent}</div>
                    </div>
                    <div class="comment-edit-form" data-role="${Roles.COMMENT_EDIT_FORM}" style="display: none;">
                        <form class="edit-comment-form" data-role="${Roles.COMMENT_EDIT_FORM_INNER}" data-comment-id="${commentId}">
                            <div class="edit-input-wrapper">
                                <textarea name="content" class="edit-textarea" data-role="${Roles.COMMENT_EDIT_TEXTAREA}" rows="3">${CommonUtils.escapeHtml(commentContent)}</textarea>
                                <div class="edit-actions">
                                    <button type="button" class="btn cancel" data-role="${Roles.COMMENT_EDIT_CANCEL}">
                                        <span data-translate="cancel">ยกเลิก</span>
                                    </button>
                                    <button type="submit" class="btn save edit-save-btn">
                                        <span data-translate="save">บันทึก</span>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div class="comment-actions">
                        <button type="button" class="action-btn like-btn ${likeClass}" data-role="${Roles.LIKE_BUTTON}"
                            data-board-id="${boardId}"
                            data-post-id="${postId}"
                            data-comment-id="${commentId}"
                            data-like-scope="comment"
                            data-is-liked="${isLiked ? "true" : "false"}"
                            data-no-spinner
                            aria-pressed="${isLiked ? "true" : "false"}"
                            title="${likeTitle}">
                            <i class="bi ${iconClass}" data-role="${Roles.LIKE_ICON}"></i>
                            <span class="like-count" data-role="${Roles.LIKE_COUNT}">${likesCount}</span>
                        </button>
                        ${
                            repliesCount > 0
                                ? `
                        <button type="button" class="comment-action-btn replies-btn" data-role="${Roles.COMMENT_REPLY_TOGGLE}" data-comment-id="${commentId}">
                            <i class="bi bi-chat"></i>
                            <span data-translate="replies">ตอบกลับ (${repliesCount})</span>
                        </button>
                        `
                                : ""
                        }
                    </div>
                </div>
            </div>
        </div>
    `;
    }

    /**
     * Render individual reply item
     */
    public renderReplyItem(reply: RenderableComment): string {
        const replyId = reply.comment_id || reply.id;
        const _userId = reply.user_id || reply.user?.id;
        const userName = reply.user ? reply.user.display_name || reply.user.name : "Anonymous";

        return `
        <div class="comment-item reply-item" data-role="${Roles.COMMENT_ITEM}" data-comment-id="${replyId}">
            <div class="comment-header">
                <div class="comment-avatar">
                    ${this.renderAvatar(reply.user)}
                </div>
                <div class="comment-content">
                    <div class="comment-user-info">
                        <span class="comment-username">${CommonUtils.escapeHtml(userName)}</span>
                        <span class="comment-timestamp timestamp-relative" data-role="${Roles.COMMENT_TIMESTAMP}" data-pending-display="1" data-timestamp="${reply.created_at}" title="${CommonUtils.utcToLocale(reply.created_at)}" style="opacity: 0;">${timestampHelper.getRelativeTime(reply.created_at)}</span>
                    </div>
                    <div class="comment-text">${CommonUtils.escapeHtml(String(reply.content || ""))}</div>
                </div>
            </div>
        </div>
    `;
    }

    /**
     * Render replies HTML
     */
    public renderReplies(replies?: RenderableComment[]): string {
        if (!replies || !replies.length) {
            return '<p class="no-replies" data-translate="no_replies">ไม่มีการตอบกลับ</p>';
        }

        return replies.map((reply) => this.renderReplyItem(reply)).join("");
    }

    /**
     * Render multiple comments as HTML
     */
    public renderCommentsHTML(comments: RenderableComment[]): string {
        if (!Array.isArray(comments)) {
            return "";
        }

        return comments.map((comment) => this.renderCommentItem(comment)).join("");
    }

    /**
     * Render no comments placeholder
     */
    public renderNoComments(): string {
        return `
            // <div class="no-comments" data-role="${Roles.COMMENT_EMPTY}">
            //     <p data-translate="no_comments">ไม่มีความคิดเห็น</p>
            // </div>
        `;
    }

    /**
     * Render no comments yet placeholder (for parent reload)
     */
    public renderNoCommentsYet(): string {
        return `<p class="text-muted" data-role="${Roles.COMMENT_EMPTY_YET}" data-translate="no_comments_yet">ยังไม่มีความคิดเห็น</p>`;
    }

    /**
     * Render loading comments indicator
     */
    public renderLoadingComments(): string {
        return '<div class="loading-comments">Refreshing comments...</div>';
    }

    /**
     * Render error loading comments message
     */
    public RenderrorLoadingComments(errorMessage = ""): string {
        if (errorMessage) {
            return `
                <div class="error-loading-comments">
                    <p>Error refreshing comments: ${CommonUtils.escapeHtml(errorMessage)}</p>
                    <p>Please refresh the page to try again.</p>
                </div>
            `;
        }
        return `
            <div class="error-loading-comments">
                <p>Rendering system not available. Please refresh the page.</p>
            </div>
        `;
    }

    /**
     * Render replies loading indicator
     */
    public renderRepliesLoading(repliesCount = 0): string {
        return `<p class="text-muted" data-role="${Roles.COMMENT_REPLIES_LOADING}">Replies loaded: ${repliesCount}</p>`;
    }

    /**
     * Render replies load error message
     */
    public renderRepliesLoadError(): string {
        return '<p class="text-error" data-translate="replies_load_error">ไม่สามารถโหลดการตอบกลับได้</p>';
    }
}

// Initialize and export globally
const commentRender = new CommentRender();

export { CommentRender, commentRender };
